import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Wallet, Shield, RefreshCw, Send, Mail, MessageCircle, Building2 } from "lucide-react";
import { FaBitcoin } from "react-icons/fa";
import { SiTether } from "react-icons/si";

export const CryptoAddressesSection = ({ cryptoSettings: initial, fetchData }) => {
  const [settings, setSettings] = useState(initial);
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/admin/settings/crypto`, settings, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Crypto addresses saved");
    } catch { toast.error("Failed to save"); }
  };

  return (
    <Card className="bg-white border-slate-200 max-w-2xl">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-slate-800 font-heading flex items-center gap-2"><Wallet className="w-5 h-5" /> Crypto Deposit Addresses</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-5">
        <p className="text-slate-500 text-sm">Configure wallet addresses shown to users for crypto deposits.</p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2"><FaBitcoin className="w-5 h-5 text-amber-500" /> Bitcoin (BTC) Address</Label>
            <Input value={settings.btc_address} onChange={(e) => setSettings({ ...settings, btc_address: e.target.value })} className="font-mono text-sm" data-testid="btc-address-input" />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2"><SiTether className="w-5 h-5 text-emerald-500" /> USDT (TRC20) Address</Label>
            <Input value={settings.usdt_trc20_address} onChange={(e) => setSettings({ ...settings, usdt_trc20_address: e.target.value })} className="font-mono text-sm" data-testid="usdt-address-input" />
          </div>
          <div className="space-y-2 pt-4 border-t border-slate-100">
            <Label className="flex items-center gap-2"><Shield className="w-5 h-5 text-rose-500" /> Government VAT Wallet</Label>
            <Input value={settings.government_wallet} onChange={(e) => setSettings({ ...settings, government_wallet: e.target.value })} className="font-mono text-sm" data-testid="government-wallet-input" />
            <p className="text-xs text-slate-500">7.5% VAT is deducted from all approved withdrawals</p>
          </div>
        </div>
        <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-crypto-btn">Save Addresses</Button>
      </CardContent>
    </Card>
  );
};

export const BankAccountSection = ({ bankSettings: initial, fetchData }) => {
  const [settings, setSettings] = useState(initial || { bank_name: "", account_name: "", account_number: "", routing_number: "", swift_code: "", bank_address: "" });
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/admin/settings/bank-account`, settings, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Bank account settings saved");
    } catch { toast.error("Failed to save"); }
  };

  return (
    <Card className="bg-white border-slate-200 max-w-2xl">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-slate-800 font-heading flex items-center gap-2"><Building2 className="w-5 h-5" /> Platform Bank Account</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-5">
        <p className="text-slate-500 text-sm">Configure the bank account shown to users for bank transfer deposits. Users will transfer funds to this account.</p>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Bank Name</Label>
              <Input value={settings.bank_name} onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })} placeholder="e.g., First Bank" data-testid="bank-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Account Name</Label>
              <Input value={settings.account_name} onChange={(e) => setSettings({ ...settings, account_name: e.target.value })} placeholder="AndinvestTrading Ltd" data-testid="bank-acct-name-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Account Number</Label>
              <Input value={settings.account_number} onChange={(e) => setSettings({ ...settings, account_number: e.target.value })} placeholder="1234567890" className="font-mono" data-testid="bank-acct-number-input" />
            </div>
            <div className="space-y-2">
              <Label>Routing Number</Label>
              <Input value={settings.routing_number} onChange={(e) => setSettings({ ...settings, routing_number: e.target.value })} placeholder="021000021" className="font-mono" data-testid="bank-routing-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SWIFT Code</Label>
              <Input value={settings.swift_code} onChange={(e) => setSettings({ ...settings, swift_code: e.target.value })} placeholder="BOFAUS3N" className="font-mono" data-testid="bank-swift-input" />
            </div>
            <div className="space-y-2">
              <Label>Bank Address</Label>
              <Input value={settings.bank_address} onChange={(e) => setSettings({ ...settings, bank_address: e.target.value })} placeholder="123 Main St, City" data-testid="bank-address-input" />
            </div>
          </div>
        </div>
        <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-bank-btn">Save Bank Account</Button>
      </CardContent>
    </Card>
  );
};

export const EmailProviderSection = ({ emailSettings: initialEmail, sendgridSettings: initialSg, twilioSettings: initialTw, fetchData }) => {
  const [emailProvider, setEmailProvider] = useState(initialSg?.api_key ? "sendgrid" : "smtp");
  const [emailSettings, setEmailSettings] = useState(initialEmail);
  const [sendgridSettings, setSendgridSettings] = useState(initialSg || { api_key: "", sender_email: "admin@andinvesttrading.com", sender_name: "" });
  const [twilioSettings, setTwilioSettings] = useState(initialTw || { account_sid: "", auth_token: "", from_number: "" });
  const [testRecipient, setTestRecipient] = useState("");
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingSendgrid, setTestingSendgrid] = useState(false);
  const [savingSendgrid, setSavingSendgrid] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleSaveEmail = async () => {
    try {
      await axios.put(`${API}/admin/settings/email`, emailSettings, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("SMTP settings saved");
    } catch { toast.error("Failed to save"); }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const body = testRecipient.trim() ? { to_email: testRecipient.trim() } : {};
      const res = await axios.post(`${API}/admin/settings/email/test`, body, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(res.data.message);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to send test email"); }
    finally { setTestingEmail(false); }
  };

  const handleSaveSendgrid = async () => {
    setSavingSendgrid(true);
    try {
      await axios.put(`${API}/admin/settings/sendgrid`, sendgridSettings, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("SendGrid settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSavingSendgrid(false); }
  };

  const handleTestSendgrid = async () => {
    setTestingSendgrid(true);
    try {
      const body = testRecipient.trim() ? { to_email: testRecipient.trim() } : {};
      const res = await axios.post(`${API}/admin/settings/sendgrid/test`, body, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(res.data.message);
    } catch (error) { toast.error(error.response?.data?.detail || "SendGrid test failed"); }
    finally { setTestingSendgrid(false); }
  };

  const handleSaveTwilio = async () => {
    try {
      await axios.put(`${API}/admin/settings/twilio`, twilioSettings, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Twilio settings saved");
    } catch { toast.error("Failed to save"); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Provider Toggle */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-slate-800 font-heading flex items-center gap-2"><Mail className="w-5 h-5" /> Email Provider</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="flex gap-3">
            <button onClick={() => setEmailProvider("sendgrid")} className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${emailProvider === "sendgrid" ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`} data-testid="provider-sendgrid-btn">
              <p className="font-medium text-slate-700 text-sm">SendGrid API</p>
              <p className="text-xs text-slate-500">Recommended</p>
            </button>
            <button onClick={() => setEmailProvider("smtp")} className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${emailProvider === "smtp" ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`} data-testid="provider-smtp-btn">
              <p className="font-medium text-slate-700 text-sm">SMTP</p>
              <p className="text-xs text-slate-500">Gmail, Outlook, Zoho</p>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* SendGrid */}
      {emailProvider === "sendgrid" && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-slate-800 font-heading text-base">SendGrid Configuration</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2"><Label>API Key</Label><Input type="password" placeholder="SG.xxxxx..." value={sendgridSettings.api_key} onChange={(e) => setSendgridSettings({ ...sendgridSettings, api_key: e.target.value })} className="font-mono text-sm" data-testid="sendgrid-api-key-input" /></div>
            <div className="space-y-2"><Label>Sender Email</Label><Input type="email" value={sendgridSettings.sender_email} onChange={(e) => setSendgridSettings({ ...sendgridSettings, sender_email: e.target.value })} data-testid="sendgrid-sender-input" /></div>
            <div className="space-y-2"><Label>Send From Name</Label><Input value={sendgridSettings.sender_name || ""} onChange={(e) => setSendgridSettings({ ...sendgridSettings, sender_name: e.target.value })} placeholder="AndinvestTrading" data-testid="sendgrid-sender-name-input" /></div>
            <div className="flex gap-3 items-end">
              <Button onClick={handleSaveSendgrid} className="bg-amber-500 hover:bg-amber-600 text-white" disabled={savingSendgrid} data-testid="save-sendgrid-btn">
                {savingSendgrid ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}Save
              </Button>
              <div className="flex-1 flex items-end gap-2">
                <div className="flex-1 space-y-1"><Label className="text-xs">Test Recipient</Label><Input type="email" placeholder="recipient@example.com" value={testRecipient} onChange={(e) => setTestRecipient(e.target.value)} data-testid="sendgrid-test-recipient" /></div>
                <Button variant="outline" className="border-slate-300 shrink-0" onClick={handleTestSendgrid} disabled={testingSendgrid} data-testid="test-sendgrid-btn">
                  {testingSendgrid ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Test
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SMTP */}
      {emailProvider === "smtp" && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-slate-800 font-heading text-base">SMTP Configuration</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2"><Label>Sender Email</Label><Input type="email" value={emailSettings.smtp_email} onChange={(e) => setEmailSettings({ ...emailSettings, smtp_email: e.target.value })} data-testid="smtp-email-input" /></div>
            <div className="space-y-2"><Label>SMTP Password</Label><Input type="password" value={emailSettings.smtp_password} onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })} data-testid="smtp-password-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Host</Label><Input value={emailSettings.smtp_host} onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })} data-testid="smtp-host-input" /></div>
              <div className="space-y-2"><Label>Port</Label><Input type="number" value={emailSettings.smtp_port} onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) || 587 })} data-testid="smtp-port-input" /></div>
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="use_ssl" checked={emailSettings.use_ssl || false} onChange={(e) => setEmailSettings({ ...emailSettings, use_ssl: e.target.checked, smtp_port: e.target.checked ? 465 : 587 })} className="w-4 h-4 accent-amber-500" />
              <Label htmlFor="use_ssl" className="cursor-pointer">Use SSL (port 465)</Label>
            </div>
            <div className="flex gap-3 items-end">
              <Button onClick={handleSaveEmail} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-email-btn">Save</Button>
              <div className="flex-1 flex items-end gap-2">
                <div className="flex-1 space-y-1"><Label className="text-xs">Test Recipient</Label><Input type="email" value={testRecipient} onChange={(e) => setTestRecipient(e.target.value)} data-testid="test-recipient-input" /></div>
                <Button variant="outline" className="border-slate-300 shrink-0" onClick={handleTestEmail} disabled={testingEmail} data-testid="test-email-btn">
                  {testingEmail ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}Test
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Twilio */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-slate-800 font-heading text-base flex items-center gap-2"><MessageCircle className="w-5 h-5" /> Twilio (SMS)</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="space-y-2"><Label>Account SID</Label><Input value={twilioSettings.account_sid} onChange={(e) => setTwilioSettings({ ...twilioSettings, account_sid: e.target.value })} className="font-mono text-sm" data-testid="twilio-sid-input" /></div>
          <div className="space-y-2"><Label>Auth Token</Label><Input type="password" value={twilioSettings.auth_token} onChange={(e) => setTwilioSettings({ ...twilioSettings, auth_token: e.target.value })} className="font-mono text-sm" data-testid="twilio-token-input" /></div>
          <div className="space-y-2"><Label>From Phone Number</Label><Input value={twilioSettings.from_number} onChange={(e) => setTwilioSettings({ ...twilioSettings, from_number: e.target.value })} data-testid="twilio-number-input" /></div>
          <Button onClick={handleSaveTwilio} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-twilio-btn">Save Twilio Settings</Button>
        </CardContent>
      </Card>
    </div>
  );
};

export const TelegramSection = ({ telegramSettings: initial, fetchData }) => {
  const [settings, setSettings] = useState(initial);
  const [testing, setTesting] = useState(false);
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/admin/settings/telegram`, settings, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Telegram settings saved");
    } catch { toast.error("Failed to save"); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/admin/settings/telegram/test`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(res.data.message);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to send"); }
    finally { setTesting(false); }
  };

  return (
    <Card className="bg-white border-slate-200 max-w-2xl">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-slate-800 font-heading flex items-center gap-2"><Send className="w-5 h-5" /> Telegram Notifications</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-5">
        <p className="text-slate-500 text-sm">Get notified on Telegram for new KYC submissions, withdrawal requests, deposits, and support messages.</p>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Bot Token</Label><Input type="password" value={settings.bot_token} onChange={(e) => setSettings({ ...settings, bot_token: e.target.value })} className="font-mono text-sm" data-testid="telegram-bot-token-input" /><p className="text-xs text-slate-500">Get from @BotFather</p></div>
          <div className="space-y-2"><Label>Chat ID</Label><Input value={settings.chat_id} onChange={(e) => setSettings({ ...settings, chat_id: e.target.value })} data-testid="telegram-chat-id-input" /><p className="text-xs text-slate-500">Group or channel ID</p></div>
        </div>
        <div className="flex gap-3">
          <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-telegram-btn">Save Settings</Button>
          <Button variant="outline" className="border-slate-300" onClick={handleTest} disabled={testing} data-testid="test-telegram-btn">
            {testing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Send Test
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const NotificationsSection = ({ notifications: initial, fetchData }) => {
  const [emailNotifications, setEmailNotifications] = useState(initial);
  const [saving, setSaving] = useState(false);
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/notifications`, { notifications: emailNotifications }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Notification settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const toggles = [
    { key: "welcome_email", label: "Welcome Email", desc: "Sends verification link on signup" },
    { key: "deposit_confirmed", label: "Deposit Confirmed", desc: "Notifies user when deposit is approved" },
    { key: "withdrawal_approved", label: "Withdrawal Approved", desc: "Notifies user with VAT breakdown" },
    { key: "withdrawal_rejected", label: "Withdrawal Rejected", desc: "Notifies user of rejection" },
    { key: "investment_created", label: "Investment Created", desc: "Confirms new investment" },
    { key: "investment_matured", label: "Investment Matured", desc: "Alerts user of matured investment" },
    { key: "kyc_approved", label: "KYC Approved", desc: "Notifies user of KYC approval" },
    { key: "kyc_rejected", label: "KYC Rejected", desc: "Notifies user of KYC rejection" },
  ];

  return (
    <Card className="bg-white border-slate-200 max-w-2xl">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-slate-800 font-heading flex items-center gap-2"><Mail className="w-5 h-5" /> Automated Notifications</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-5">
        <p className="text-slate-500 text-sm">Toggle which events automatically send email notifications to users.</p>
        <div className="space-y-2">
          {toggles.map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`notif-toggle-${key}`}>
              <div>
                <p className="text-sm font-medium text-slate-700">{label}</p>
                <p className="text-xs text-slate-500">{desc}</p>
              </div>
              <button
                onClick={() => setEmailNotifications((prev) => ({ ...prev, [key]: !prev[key] }))}
                className={`relative w-11 h-6 rounded-full transition-colors ${emailNotifications[key] ? "bg-emerald-500" : "bg-slate-300"}`}
                data-testid={`notif-switch-${key}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${emailNotifications[key] ? "translate-x-5" : ""}`} />
              </button>
            </div>
          ))}
        </div>
        <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-white" disabled={saving} data-testid="save-notifications-btn">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}Save Notification Settings
        </Button>
      </CardContent>
    </Card>
  );
};


export const NotificationTemplatesSection = ({ fetchData }) => {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useState(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/notification-templates`, { headers: getAuthHeaders(), withCredentials: true });
        setTemplates(res.data.templates || {});
      } catch {} finally { setLoading(false); }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/notification-templates`, { templates }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Templates saved successfully");
    } catch { toast.error("Failed to save templates"); }
    finally { setSaving(false); }
  };

  const templateKeys = Object.keys(templates);

  if (loading) return <div className="text-slate-500 text-center py-8">Loading templates...</div>;

  return (
    <Card className="bg-white border-slate-200 max-w-3xl">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-slate-800 font-heading flex items-center gap-2"><Mail className="w-5 h-5" /> Email Templates</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        <p className="text-slate-500 text-sm">Edit the subject and body for each automated email notification. These templates are used when the corresponding notification is enabled.</p>
        {templateKeys.map((key) => (
          <div key={key} className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3" data-testid={`template-${key}`}>
            <p className="text-sm font-semibold text-slate-700 capitalize">{key.replace(/_/g, " ")}</p>
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Subject</Label>
              <Input value={templates[key]?.subject || ""} onChange={(e) => setTemplates((prev) => ({ ...prev, [key]: { ...prev[key], subject: e.target.value } }))} className="text-sm" data-testid={`template-subject-${key}`} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Body</Label>
              <textarea value={templates[key]?.body || ""} onChange={(e) => setTemplates((prev) => ({ ...prev, [key]: { ...prev[key], body: e.target.value } }))} className="w-full p-3 border border-slate-200 rounded-md text-sm resize-none h-24" data-testid={`template-body-${key}`} />
            </div>
          </div>
        ))}
        <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-white" disabled={saving} data-testid="save-templates-btn">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}Save All Templates
        </Button>
      </CardContent>
    </Card>
  );
};

export const ManualPushSection = ({ users, fetchData }) => {
  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [bulkFilter, setBulkFilter] = useState("all");
  const [sendingBulk, setSendingBulk] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleSendSingle = async () => {
    if (!toEmail || !message) { toast.error("Email and message are required"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/send-notification`, { to_email: toEmail, subject: subject || "Notification from AndInvestTrading", message }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(res.data.message);
      setToEmail(""); setSubject(""); setMessage("");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  const handleSendBulk = async () => {
    if (!message) { toast.error("Message is required"); return; }
    if (!confirm(`Send this notification to ${bulkFilter === "all" ? "ALL users" : bulkFilter + " users"}?`)) return;
    setSendingBulk(true);
    try {
      const res = await axios.post(`${API}/admin/send-bulk-notification`, { filter: bulkFilter, subject: subject || "Important Update from AndInvestTrading", message }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(res.data.message);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to send"); }
    finally { setSendingBulk(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Single User Push */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-slate-800 font-heading text-base flex items-center gap-2"><Send className="w-5 h-5" /> Send to User</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="space-y-2">
            <Label>Recipient Email</Label>
            <div className="flex gap-2">
              <Input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="user@example.com" className="flex-1" data-testid="push-email-input" />
              <select onChange={(e) => setToEmail(e.target.value)} className="h-10 px-3 border border-slate-200 rounded-md text-sm min-w-[180px]" data-testid="push-user-select">
                <option value="">Select user...</option>
                {users?.map((u) => (
                  <option key={u.user_id} value={u.email}>{u.name} ({u.email})</option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Notification from AndInvestTrading" data-testid="push-subject-input" />
          </div>
          <div className="space-y-2">
            <Label>Message</Label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} className="w-full p-3 border border-slate-200 rounded-md text-sm resize-none h-28" placeholder="Type your message..." data-testid="push-message-input" />
          </div>
          <Button onClick={handleSendSingle} className="bg-amber-500 hover:bg-amber-600 text-white gap-2" disabled={sending} data-testid="send-push-btn">
            {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send Notification
          </Button>
        </CardContent>
      </Card>

      {/* Bulk Notification */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-slate-800 font-heading text-base flex items-center gap-2"><Mail className="w-5 h-5" /> Bulk Notification</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="space-y-2">
            <Label>Send To</Label>
            <select value={bulkFilter} onChange={(e) => setBulkFilter(e.target.value)} className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm" data-testid="bulk-filter-select">
              <option value="all">All Users</option>
              <option value="verified">Verified Users Only</option>
              <option value="unverified">Unverified Users Only</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Important Update" />
          </div>
          <div className="space-y-2">
            <Label>Message</Label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} className="w-full p-3 border border-slate-200 rounded-md text-sm resize-none h-28" placeholder="Type your message..." />
          </div>
          <Button onClick={handleSendBulk} className="bg-rose-500 hover:bg-rose-600 text-white gap-2" disabled={sendingBulk} data-testid="send-bulk-btn">
            {sendingBulk ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Send to {bulkFilter === "all" ? "All Users" : bulkFilter + " Users"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
